import React, { createContext, useContext, useReducer, useCallback } from 'react'
import {
  createInitialState,
  normalizeDayTimeboxes,
  WEEKDAYS,
  FIRST_SLOT,
  LAST_SLOT,
} from '../data/seed'
import { safeId } from '../utils/safeId'

const AppStateContext = createContext(null)

export function reducer(state, action) {
  switch (action.type) {
    case 'LOAD_STATE':
      return action.payload

    case 'SET_GOAL':
      return {
        ...state,
        goals: { ...state.goals, [action.key]: action.value },
      }

    case 'SET_DAY_TIMEBOXES': {
      const { day, timeboxes } = action.payload
      const normalized = normalizeDayTimeboxes(timeboxes)
      const existing = state.days[day] || {}
      return {
        ...state,
        days: { ...state.days, [day]: { ...existing, timeboxes: normalized } },
      }
    }

    case 'ADD_TIMEBOX': {
      const { day, typeId, afterSlot } = action.payload
      const list = state.days[day]?.timeboxes ?? []
      const startSlot = afterSlot != null ? afterSlot + 1 : FIRST_SLOT
      const endSlot = Math.min(startSlot + 1, LAST_SLOT + 1)
      if (startSlot > LAST_SLOT) return state
      const used = new Set()
      list.forEach(t => {
        for (let s = t.startSlot; s < t.startSlot + (t.lengthSlots ?? 1); s++) used.add(s)
      })
      let insert = startSlot
      while (used.has(insert) && insert <= LAST_SLOT) insert++
      if (insert > LAST_SLOT) return state
      const newBox = {
        id: safeId(),
        typeId,
        startSlot: insert,
        lengthSlots: 1,
      }
      const next = [...list, newBox].sort((a, b) => a.startSlot - b.startSlot)
      const existingAdd = state.days[day] || {}
      return {
        ...state,
        days: { ...state.days, [day]: { ...existingAdd, timeboxes: normalizeDayTimeboxes(next) } },
      }
    }

    case 'REMOVE_TIMEBOX': {
      const { day, timeboxId } = action.payload
      const list = (state.days[day]?.timeboxes ?? []).filter(t => t.id !== timeboxId)
      const existingRem = state.days[day] || {}
      return {
        ...state,
        days: { ...state.days, [day]: { ...existingRem, timeboxes: normalizeDayTimeboxes(list) } },
      }
    }

    case 'MOVE_TIMEBOX': {
      const { day, timeboxId, newStartSlot } = action.payload
      const list = state.days[day]?.timeboxes ?? []
      const box = list.find(t => t.id === timeboxId)
      if (!box) return state
      const length = box.lengthSlots ?? 1
      const others = list.filter(t => t.id !== timeboxId).sort((a, b) => a.startSlot - b.startSlot)
      // Compact others: assign sequential startSlots with no gaps
      let slot = FIRST_SLOT
      const compact = others.map(t => {
        const out = { ...t, startSlot: slot }
        slot += t.lengthSlots ?? 1
        return out
      })
      // Insert index so the moved block starts at newStartSlot (cumulative length = offset)
      const targetOffset = newStartSlot - FIRST_SLOT
      let cum = 0
      let insertPos = 0
      for (let i = 0; i < compact.length; i++) {
        if (cum >= targetOffset) break
        insertPos = i + 1
        cum += compact[i].lengthSlots ?? 1
      }
      insertPos = Math.max(0, Math.min(compact.length, insertPos))
      const movedBox = { ...box, startSlot: 0, lengthSlots: length, noAutoMergeWithPrev: true }
      const inserted = [...compact.slice(0, insertPos), movedBox, ...compact.slice(insertPos)]
      slot = FIRST_SLOT
      const withSlots = inserted.map(t => {
        const out = { ...t, startSlot: slot }
        slot += t.lengthSlots ?? 1
        return out
      })
      const next = normalizeDayTimeboxes(withSlots)
      const existingMove = state.days[day] || {}
      return {
        ...state,
        days: { ...state.days, [day]: { ...existingMove, timeboxes: next } },
      }
    }

    case 'RESIZE_TIMEBOX': {
      const { day, timeboxId, lengthSlots } = action.payload
      const list = state.days[day]?.timeboxes ?? []
      const box = list.find(t => t.id === timeboxId)
      if (!box) return state
      const len = Math.max(1, Math.min(lengthSlots, LAST_SLOT - box.startSlot + 1))
      const others = list.filter(t => t.id !== timeboxId)
      const used = new Set()
      others.forEach(t => {
        for (let s = t.startSlot; s < t.startSlot + (t.lengthSlots ?? 1); s++) used.add(s)
      })
      for (let s = box.startSlot; s < box.startSlot + len; s++) if (used.has(s)) return state
      const updated = { ...box, lengthSlots: len }
      const next = normalizeDayTimeboxes([...others, updated])
      const existingResize = state.days[day] || {}
      return { ...state, days: { ...state.days, [day]: { ...existingResize, timeboxes: next } } }
    }

    case 'SET_DAY_LOCKED': {
      const { day, locked } = action.payload
      const existing = state.days[day] || {}
      return {
        ...state,
        days: { ...state.days, [day]: { ...existing, locked: !!locked } },
      }
    }

    case 'SPLIT_TIMEBOX': {
      const { day, timeboxId, splitAfterSlot } = action.payload
      const list = state.days[day]?.timeboxes ?? []
      const box = list.find(t => t.id === timeboxId)
      if (!box) return state
      const len = box.lengthSlots ?? 1
      const start = box.startSlot
      if (len < 2) return state
      if (splitAfterSlot < start || splitAfterSlot >= start + len - 1) return state
      const leftLen = splitAfterSlot - start + 1
      const rightLen = start + len - (splitAfterSlot + 1)
      const leftPart = { ...box, lengthSlots: leftLen }
      const rightPart = {
        id: safeId(),
        typeId: box.typeId,
        startSlot: splitAfterSlot + 1,
        lengthSlots: rightLen,
        intention: box.intention ?? '',
        ignored: box.ignored ?? false,
        noAutoMergeWithPrev: true,
      }
      const newList = list.flatMap(t => t.id === timeboxId ? [leftPart, rightPart] : [t])
        .sort((a, b) => a.startSlot - b.startSlot)
      const next = normalizeDayTimeboxes(newList)
      const existingSplit = state.days[day] || {}
      return {
        ...state,
        days: { ...state.days, [day]: { ...existingSplit, timeboxes: next } },
      }
    }

    case 'MERGE_TIMEBOXES': {
      const { day, leftTimeboxId, rightTimeboxId } = action.payload
      const list = state.days[day]?.timeboxes ?? []
      const left = list.find(t => t.id === leftTimeboxId)
      const right = list.find(t => t.id === rightTimeboxId)
      if (!left || !right) return state
      if (left.typeId !== right.typeId) return state
      const leftEnd = left.startSlot + (left.lengthSlots ?? 1)
      if (leftEnd !== right.startSlot) return state
      const merged = {
        ...left,
        lengthSlots: (left.lengthSlots ?? 1) + (right.lengthSlots ?? 1),
        noAutoMergeWithPrev: false,
      }
      const newList = list.filter(t => t.id !== rightTimeboxId).map(t => t.id === leftTimeboxId ? merged : t)
      const next = normalizeDayTimeboxes(newList)
      const existingMerge = state.days[day] || {}
      return {
        ...state,
        days: { ...state.days, [day]: { ...existingMerge, timeboxes: next } },
      }
    }

    default:
      return state
  }
}

export function AppStateProvider({ children, initialState }) {
  const [state, dispatch] = useReducer(reducer, initialState ?? createInitialState())

  const setGoal = useCallback((key, value) => dispatch({ type: 'SET_GOAL', key, value }), [])
  const setDayTimeboxes = useCallback((day, timeboxes) =>
    dispatch({ type: 'SET_DAY_TIMEBOXES', payload: { day, timeboxes } }), [])
  const addTimebox = useCallback((day, typeId, afterSlot) =>
    dispatch({ type: 'ADD_TIMEBOX', payload: { day, typeId, afterSlot } }), [])
  const removeTimebox = useCallback((day, timeboxId) =>
    dispatch({ type: 'REMOVE_TIMEBOX', payload: { day, timeboxId } }), [])
  const moveTimebox = useCallback((day, timeboxId, newStartSlot) =>
    dispatch({ type: 'MOVE_TIMEBOX', payload: { day, timeboxId, newStartSlot } }), [])
  const resizeTimebox = useCallback((day, timeboxId, lengthSlots) =>
    dispatch({ type: 'RESIZE_TIMEBOX', payload: { day, timeboxId, lengthSlots } }), [])
  const setDayLocked = useCallback((day, locked) =>
    dispatch({ type: 'SET_DAY_LOCKED', payload: { day, locked } }), [])
  const splitTimebox = useCallback((day, timeboxId, splitAfterSlot) =>
    dispatch({ type: 'SPLIT_TIMEBOX', payload: { day, timeboxId, splitAfterSlot } }), [])
  const mergeTimeboxes = useCallback((day, leftTimeboxId, rightTimeboxId) =>
    dispatch({ type: 'MERGE_TIMEBOXES', payload: { day, leftTimeboxId, rightTimeboxId } }), [])
  const loadState = useCallback((payload) => dispatch({ type: 'LOAD_STATE', payload }), [])

  return (
    <AppStateContext.Provider
      value={{
        state,
        setGoal,
        setDayTimeboxes,
        addTimebox,
        removeTimebox,
        moveTimebox,
        resizeTimebox,
        setDayLocked,
        splitTimebox,
        mergeTimeboxes,
        loadState,
      }}
    >
      {children}
    </AppStateContext.Provider>
  )
}

export function useAppState() {
  const ctx = useContext(AppStateContext)
  if (!ctx) throw new Error('useAppState must be used within AppStateProvider')
  return ctx
}
